<template>
<div class='widget-W0119V0001'>
  <div :class="placement" class="widget">
    <h2 class="title">Meebook</h2>
    <div v-if="loading" class="widget-loader">
      <i class="fa fa-spinner"></i>
      Loading...
    </div>
    <div v-if="hasErrors">
      <ul class="errors">
        <li v-for="error in errors" v-bind:key="error" class="errors__error">
          {{ error }}
        </li>
      </ul>
    </div>
    <ul class="links">
      <li>Gå til:</li>
      <li v-for="(item, index) in data.links" :key="index">
        <!--
          GET form with target="_blank" is required by AULA's iOS app.
          Submitting a form is treated as an explicit user-action by iOS
          WKWebView, while window.open() and POST-redirects are blocked
          by the latest iOS security update. See AULA support guidance.
          Vue reactivity keeps the hidden aulaJWT input fresh — the
          shared mixin refreshes the token every 4 minutes in the background.
        -->
        <form
          :action="getApiBaseUrl() + 'relatednotificationslinkauth/'"
          method="GET"
          target="_blank"
          class="links__form">
          <input type="hidden" name="type" :value="typeIdentifier"/>
          <input type="hidden" name="sessionUUID" :value="sessionUUID"/>
          <input type="hidden" name="returnUrl"
                 :value="encodeURIComponent(getBaseUrl() + item.path + '?aulaJWT=' + aulaToken)"/>
          <input type="hidden" name="currentWeekNumber" :value="getThisYearWeek()"/>
          <input type="hidden" name="aulaJWT" :value="aulaToken"/>
          <input v-for="(inst) in institutionFilter" :key="inst" type="hidden" name="institutionFilter[]"
                 :value="inst"/>
          <input v-for="(child) in childFilter" :key="child" type="hidden" name="childFilter[]" :value="child"/>
          <button type="submit" class="links__submit">{{ item.label }}</button>
        </form>
      </li>
    </ul>
    <div class="empty-state" v-if="(data.notifications.items.length === 0)">
      <h3 class="empty-state__title">Ingen notifikationer</h3>
      <p class="empty-state__body">Når der sker noget nyt i Meebook,
        vil det blive vist her</p>
    </div>
    <ul v-else>
      <li
        v-for="item in data.notifications.items"
        :key="item.id"
        v-if="item.data.isVisibleForRelated"
        class="notification"
      >
        <div class="notification__header">
          <h2 class="notification__title">{{ renderTitle(item) }}</h2>
          <time class="notification__date">{{ renderDate(item.date) }}</time>
        </div>
        <p class="notification__subtitle">
          <span v-if="item.type === 'messagebook_comment_new'" class="notification__bubble"></span>
          {{ renderMessage(item) }}
        </p>
        <form
          :action="getApiBaseUrl() + 'relatednotificationslinkauth/'"
          method="GET"
          target="_blank"
          class="notification__form">
          <input type="hidden" name="type" :value="typeIdentifier"/>
          <input type="hidden" name="sessionUUID" :value="sessionUUID"/>
          <input type="hidden" name="returnUrl"
                 :value="encodeURIComponent(getBaseUrl() + item.link + '&aulaJWT=' + aulaToken)"/>
          <input type="hidden" name="currentWeekNumber" :value="getThisYearWeek()"/>
          <input type="hidden" name="aulaJWT" :value="aulaToken"/>
          <input v-for="(inst) in institutionFilter" :key="inst" type="hidden" name="institutionFilter[]"
                 :value="inst"/>
          <input v-for="(child) in childFilter" :key="child" type="hidden" name="childFilter[]" :value="child"/>
          <button type="submit" class="notification__link">Se på Meebook</button>
        </form>
      </li>
    </ul>
  </div>
</div>
</template>

<script>
var aulaWidgetMixin = {
  props: {
    axios: Function,
    moment: Function,
    getAulaToken: Function,
    devServer: String,
    sessionUUID: String,
    placement: String,
    currentWeekNumber: String,
    isMobileApp: Boolean,
    assuranceLevel: Number,
    userProfile: String,
    childFilter: [String, Array],
    institutionFilter: [String, Array],
    group: String,
    meebookImgBaseUrl: {
      type: String,
      default: 'https://app.meebook.com/images/aula/',
    },
    meebookApiBaseUrl: String,
  },
  data() {
    return {
      loading: true,
      items: [],
      errors: [],
      showAll: false,
      aulaToken: null,
    };
  },
  mounted() {
    this.aulaToken = this.getAulaToken();

// AULA JWTs live ~5 min. Keep ours fresh in the background so the
// hidden form inputs (which embed the token at render time) are
// always valid when the user clicks. Vue's reactivity updates the
// inputs automatically when this.aulaToken changes.
//
// NOTE: AULA support has explicitly disallowed addEventListener()
// on document or window in widgets, so we rely on setInterval alone.
    this._aulaTokenRefreshInterval = setInterval(() => {
      this.refreshAulaTokenSilently();
    }, 4 * 60 * 1000);
  },
  beforeDestroy() {
    if (this._aulaTokenRefreshInterval) {
      clearInterval(this._aulaTokenRefreshInterval);
    }
  },
  watch: {
    aulaToken: function (newVal, oldVal) {
// Only trigger loadContent on the initial token arrival (empty -> value).
// Subsequent token changes (from ensureAulaToken during 401 retry, paging,
// or time-based refresh) must NOT re-trigger loadContent — the widget's
// own isRetry logic handles reloads and caps them at 1 retry.
      if (newVal && !oldVal) {
        this.loadContent();
      }
    },
  },
  computed: {
    maxShown() {
      return this.showAll ? Infinity : 3;
    },
    hasErrors() {
      return this.errors && this.errors.length;
    },
  },
  methods: {
    async ensureAulaToken() {
      const fresh = await this.getAulaToken();
// Don't clobber a valid token with null/undefined — AULA can briefly
// return empty values while a new token is being negotiated.
      if (fresh) {
        this.aulaToken = fresh;
      }
      return this.aulaToken;
    },
    async refreshAulaTokenSilently() {
// Background refresh — never surface errors to the user. The 401
// retry path in loadContent() is still our backstop.
      try {
        const fresh = await this.getAulaToken();
        if (fresh) {
          this.aulaToken = fresh;
        }
      } catch (e) { /* swallow */
      }
    },
    async getClient() {
      await this.ensureAulaToken();
      return this.axios.create({
        baseURL: this.getApiBaseUrl(),
        timeout: 30000,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Accept': 'application/json',
          'Authorization': 'Bearer ' + this.aulaToken,
          'sessionUUID': this.sessionUUID,
          'X-Version': '1.0',
        },
      });
    },
    getApiBaseUrl() {
      return "https://app.meebook.com/aulaapi/";
    },
    getBookIcon() {
      return 'background-image: url(' + this.meebookImgBaseUrl + 'meebook-2x.png)';
    },
    toggleShowAll() {
      this.showAll = !this.showAll;
    },
  },
};
export default {
  mixins: [aulaWidgetMixin],
  data() {
    return {
      data: {
        notifications: {items: [], pagination: []},
        links: [],
      },
      loading: true,
      widgetIdentifier: 'NotificationWidget-',
      typeIdentifier: 'linkauth'
    };
  },
  methods: {
    getBaseUrl() {
      return "https://app.meebook.com";
    },
    getThisYearWeek() {
      const now = new Date();
      const year = now.getFullYear();
      const start = new Date(year, 0, 1);
      const diff = now - start;
      const oneDay = 1000 * 60 * 60 * 24;
      const dayOfYear = Math.floor(diff / oneDay);
      const week = Math.ceil(dayOfYear / 7);
      return `${year}-W${week < 10 ? '0' + week : week}`;
    },
    async loadContent(isRetry = false) {
      this.errors = [];

      try {
        let http = await this.getClient();

        const response = await http.get("relatednotifications", {
          params: {
            currentWeekNumber: this.getThisYearWeek(),
            userProfile: this.userProfile,
            childFilter: this.childFilter,
            institutionFilter: this.institutionFilter,
          },
        });

        this.loading = false;
        this.data = response.data || [];

      } catch (error) {
        // 401 EXPIRED TOKEN LOGIC
        if (error.response && error.response.status === 401 && !isRetry) {
          this.loading = true;
          this.errors.push("Din Aula-token fornyes.. Øjeblik");

          try {
            await this.ensureAulaToken();

            this.errors.pop();

            return this.loadContent(true);
          } catch (retryError) {
            this.errors.pop();
          }
        }

        // STANDARD ERROR LOGIC
        this.loading = false;
        this.data = [];
        if (error.response && error.response.data && error.response.data.exceptionMessage) {
          this.errors.push(error.response.data.exceptionMessage);
        } else {
          this.errors.push("Notifikationer kunne ikke hentes");
        }
      }
    },
    renderTitle(item) {
      switch (item.type) {
        case "messagebook_comment_new":
          return (
            this.possesiveFirstname(item.data.studentName) + " meddelelsesbog"
          );
        case "messagebook_published":
          return (
            this.possesiveFirstname(item.data.studentName) + " meddelelsesbog"
          );
        case "annualplan_published":
          return this.possesiveFirstname(item.data.studentName) + " årsplan";
        case "upv_published":
          return this.possesiveFirstname(item.data.studentName) + " UPV";
        default:
          return "Unknown notification";
      }
    },
    renderMessage(item) {
      switch (item.type) {
        case "messagebook_comment_new":
          return (
            this.firstname(item.data.senderName) + ": " + item.data.excerpt
          );
        case "messagebook_published":
          return `Ny meddelelsesbog for ${item.data.yearSpan}`;
        case "annualplan_published":
          return `Ny årsplan for ${item.data.categories.join(", ")}`;
        case "upv_published":
          return `Ny UPV for ${item.data.yearSpan}`;
      }
    },
    renderDate(dateStr) {
      const today = new Date();
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const date = new Date(dateStr);
      today.setHours(0, 0, 0, 0);
      date.setHours(0, 0, 0, 0);
      yesterday.setHours(0, 0, 0, 0);
      if (date.getTime() === today.getTime()) {
        return 'I dag'
      }
      if (date.getTime() === yesterday.getTime()) {
        return 'I går'
      }
      return date.toLocaleDateString('da', {
        day: 'numeric',
        month: 'short'
      })
    },
    firstname(name) {
      return name.split(" ")[0];
    },
    possesiveFirstname(name) {
      const firstname = this.firstname(name);
      return ["x", "s", "z"].includes(firstname.slice(-1))
        ? firstname + "'"
        : firstname + "s";
    },
  },
};
</script>

<style lang="scss">
.widget-W0119V0001 {
.widget-loader {
  font-family: "Lato", "Avenir", Helvetica, Arial, sans-serif;
  font-size: 16px;
  margin: 20px 0
}

.widget-loader > .fa {
  animation-name: spin;
  animation-duration: 1s;
  animation-iteration-count: infinite;
  animation-timing-function: linear;
  animation-fill-mode: both;
  margin-right: 10px
}

@keyframes spin {
  0% {
    transform: rotate(0)
  }
  100% {
    transform: rotate(360deg)
  }
}

.errors {
  margin-top: 20px
}

.errors__error {
  display: block;
  padding: 15px 20px;
  background-color: #fff;
  color: #b50050
}

* {
  box-sizing: border-box
}

ul {
  margin: 0;
  padding: 0;
  list-style-type: none
}

li {
  margin: 0;
  width: 100%
}

.widget-title {
  display: block;
  text-align: left;
  font-family: "Lato", "Avenir", Helvetica, Arial, sans-serif;
  font-size: 17px;
  font-weight: 700;
  text-transform: uppercase;
  margin-bottom: 0;
  margin-top: 30px
}

.widget {
  text-align: left
}

.widget__item__name {
  display: block;
  font-size: 14px;
  font-weight: 700;
  text-transform: capitalize;
  color: #5d6c79;
  margin-top: 30px;
  margin-bottom: 10px
}

.widgetlist {
  text-align: left
}

.widgetlist__item {
  position: relative;
  padding: 15px 20px;
  background-color: #fff;
  margin-bottom: 5px;
  cursor: pointer;
  outline: 0
}

.widgetlist__item:focus {
  background-color: rgba(255, 255, 255, .4)
}

.widgetlist__item__title {
  font-size: 16px;
  font-weight: 700
}

.widgetlist__item__subtitle {
  font-size: 11px;
  font-weight: 700;
  color: #6a7681;
  text-transform: capitalize
}

.widgetlist__item__subtitle::first-letter {
  text-transform: uppercase
}

.widgetlist__item__form {
  margin: 0;
  padding: 0
}

.widgetlist__item__link {
  display: block;
  width: 100%;
  color: inherit;
  text-decoration: none;
  outline: none;
  background: rgba(0, 0, 0, 0);
  border: 0;
  padding: 0;
  margin: 0;
  text-align: left;
  font: inherit;
  cursor: pointer
}

.widgetlist__item__link:focus-visible {
  outline: 2px solid #f5836d;
  outline-offset: -2px
}

.widgetlist__showall {
  display: block;
  padding: 0;
  margin-top: 5px;
  font-size: 14px;
  font-weight: 700;
  text-decoration: underline;
  cursor: pointer;
  background-color: rgba(0, 0, 0, 0);
  border: 0;
  outline: none
}

.widgetlist__showall:focus {
  opacity: .5
}

.title {
  background: #233f58;
  color: #fff;
  font-weight: 700;
  font-size: 16px;
  line-height: 19px;
  padding: 10px;
  margin: 0
}

.notification {
  position: relative;
  background: #fff;
  padding: 10px;
  transition: transform .15s ease-out
}

.notification + .notification {
  margin-top: 8px
}

.notification__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 0 4px
}

.notification__title {
  font-size: 14px;
  font-weight: 700;
  color: #2c3e50;
  line-height: 17px;
  margin: 0
}

.notification__subtitle {
  font-size: 12px;
  line-height: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0 0 4px;
  color: #2c3e50
}

.notification__bubble {
  content: "";
  width: 14px;
  height: 14px;
  display: inline-block;
  background-image: url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTQiIGhlaWdodD0iMTQiIHZpZXdCb3g9IjAgMCAxNCAxNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNNi42OTkwMiAxLjc1MDM1QzcuNzA4OTggMS43NDAxMSA4LjcwODUzIDEuOTU1MDMgOS42MjUwMiAyLjM3OTQ3QzEwLjQ2NDIgMi43NTUyNiAxMS4xOTM2IDMuMzM4OTIgMTEuNzQ0MyA0LjA3NTIyQzEyLjI1MTYgNC43NDg5OSAxMi41MjUzIDUuNTY5ODMgMTIuNTIzOSA2LjQxMzIyQzEyLjUyNTEgNy4yNTYzMiAxMi4yNTE0IDguMDc2ODIgMTEuNzQ0MyA4Ljc1MDM1QzExLjE5MzYgOS40ODY2NiAxMC40NjQyIDEwLjA3MDMgOS42MjUwMiAxMC40NDYxQzguNzA4NDYgMTAuODcwMiA3LjcwODkxIDExLjA4NDkgNi42OTkwMiAxMS4wNzQzQzYuMDA2MTMgMTEuMDc2OSA1LjMxNjExIDEwLjk4NTEgNC42NDgwMiAxMC44MDEzQzQuMjQ3MTQgMTEuMTI5NSAzLjgwNjA5IDExLjQwNTMgMy4zMzU1MiAxMS42MjIxQzIuOTQxMDQgMTEuODA4NiAyLjUyODkxIDExLjk1NTEgMi4xMDUyNyAxMi4wNTk2QzEuODA4MzggMTIuMTMzNiAxLjUwNzA1IDEyLjE4ODYgMS4yMDMxNSAxMi4yMjQxTDAuODc1MDIxIDEyLjI1MDNDMS4zOTA4NyAxMS43NTczIDEuODI0NzggMTEuMTg1MiAyLjE2MDQgMTAuNTU1NUMyLjMyMzUyIDEwLjI2OTUgMi40MjU3IDkuOTUyNzcgMi40NjA1MiA5LjYyNTM1QzEuOTc5NDkgOS4yMjAwNyAxLjU4NDQgOC43MjI2NCAxLjI5ODUyIDguMTYyMzVDMS4wMjA4MyA3LjYyNTc3IDAuODc1NjIxIDcuMDMwNTIgMC44NzUwMjEgNi40MjYzNUMwLjg3MjIzNSA1LjU3ODcgMS4xNDU5NSA0Ljc1MzI3IDEuNjU0NjUgNC4wNzUyMkMyLjIwNTQ0IDMuMzM5MjQgMi45MzQ4NCAyLjc1NTg5IDMuNzczOSAyLjM4MDM1QzQuNjkwMDUgMS45NTU3MyA1LjY4OTI5IDEuNzQwNTIgNi42OTkwMiAxLjc1MDM1WiIgZmlsbD0iI0RCRTNFOSIvPjwvc3ZnPg==");
  vertical-align: bottom
}

.notification__date {
  color: rgba(44, 62, 80, .5);
  font-size: 12px;
  line-height: 14px
}

.notification__form {
  margin: 0;
  padding: 0
}

.notification__link {
  font-size: 12px;
  line-height: 14px;
  font-weight: 700;
  color: #f5836d;
  text-decoration: none;
  display: block;
  background: rgba(0, 0, 0, 0);
  border: 0;
  padding: 0;
  margin: 0;
  text-align: left;
  font-family: inherit;
  cursor: pointer;
  outline: none
}

.notification__link:hover {
  text-decoration: underline
}

.notification__link:focus-visible {
  outline: 2px solid #f5836d;
  outline-offset: 2px
}

.notification__link::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0
}

.notification:hover {
  transform: translate3d(0, 0, 0) scale(1.015)
}

.links {
  display: flex;
  justify-content: flex-start;
  padding: 14px 0;
  gap: 10px
}

.links > li {
  font-size: 12.6px;
  display: block;
  width: auto;
  color: rgba(0, 0, 0, .5)
}

.links__form {
  display: inline;
  margin: 0;
  padding: 0
}

.links__submit {
  color: #f5836d;
  font-weight: bold;
  text-decoration: underline;
  background: rgba(0, 0, 0, 0);
  border: 0;
  padding: 0;
  margin: 0;
  font-family: inherit;
  font-size: inherit;
  cursor: pointer;
  outline: none
}

.links__submit:hover {
  text-decoration: unset;
  color: #e26b54
}

.links__submit:focus-visible {
  outline: 2px solid #f5836d;
  outline-offset: 2px
}

.empty-state {
  padding: 20px 10px;
  text-align: center
}

.empty-state__title {
  font-size: 14px;
  line-height: 19px;
  font-weight: 700;
  color: rgba(44, 62, 80, .8);
  margin: 0
}

.empty-state__body {
  font-size: 14px;
  line-height: 19px;
  font-weight: 400;
  color: rgba(44, 62, 80, .8);
  margin: 0
}
}
</style>
